import omitBy from 'lodash/omitBy';
import { useGlobal } from '@/utils/useGlobal';

type config = {
    timeToCount: number;
    interval: number;
    onStart?: () => void;
    onUpdate?: (timeLeft: number) => void;
    onEnd?: () => void;
};

type status = 'pending' | 'start' | 'pause';

export class CountDown {
    private config: config;
    private timeLeft?: number; // 剩余时间
    private timer?: number;
    private lastInterval: number | null = null;
    private started: number | null = null;
    private status: status = 'pending';

    constructor(config: config) {
        this.config = { ...config };
    }

    private loop(ts: number) {
        if (typeof this.timeLeft === 'undefined') return;

        if (!this.started || !this.lastInterval) {
            this.started = ts;
            this.lastInterval = ts;
        }
        const { interval, onUpdate, onEnd } = this.config;
        const localInterval = Math.min(interval, this.timeLeft || Infinity);
        if (ts - this.lastInterval >= localInterval) {
            this.lastInterval += localInterval;
            this.timeLeft = this.timeLeft - localInterval;
            onUpdate && onUpdate(this.timeLeft);
        }
        if (ts - this.started < this.config.timeToCount) {
            this.timer = window.requestAnimationFrame(this.loop.bind(this));
        } else {
            this.timeLeft = 0;
            this.started = null;
            this.lastInterval = 0;
            onEnd && onEnd();
        }
    }

    public start() {
        if (this.started) return;
        if (this.status !== 'pending') return;
        if (typeof window === 'undefined') {
            console.error('The timer can only run in the browser');
            return;
        }
        this.status = 'start';
        this.timeLeft = this.config.timeToCount;
        this.config.onUpdate && this.config.onUpdate(this.timeLeft);
        this.timer = window.requestAnimationFrame(this.loop.bind(this));
    }

    public pause() {
        if (!this.timer) return;
        if (!this.timeLeft) return;
        this.status = 'pause';
        window.cancelAnimationFrame(this.timer);
        this.started = null;
        this.lastInterval = null;
        this.config.timeToCount = this.timeLeft;
    }

    public resume() {
        if (this.status !== 'pause') return;
        if (this.timeLeft && this.timeLeft > 0) {
            this.timer && window.cancelAnimationFrame(this.timer);
            this.timer = window.requestAnimationFrame(this.loop.bind(this));
        }
    }

    public descroy() {
        if (this.timer) {
            window.cancelAnimationFrame(this.timer);
        }
    }
}

type params = {
    startTime?: string;
    endTime: string;
    interval?: number;
    onStart?: () => void;
    onUpdate?: (timeLeft: number) => void;
    onEnd?: () => void;
};

export function useCountDown({
    // startTime,
    endTime,
    onStart,
    onUpdate,
    onEnd,
    interval = 1000,
}: params) {
    const { useEffect, getSystemTime, useState } = useGlobal();
    const [timeToCount, setTimeToCount] = useState();
    const [example, setExample] = useState(null);

    const parseTimestamp = (timeStr: string) => {
        return new Date(timeStr).getTime();
    };

    useEffect(() => {
        const currTimestamp = getSystemTime();

        const endTimestamp = parseTimestamp(endTime);
        if (!endTimestamp) return;

        const timeToCount = endTimestamp - currTimestamp;
        setTimeToCount(timeToCount);
    }, []);

    useEffect(() => {
        if (!timeToCount) return;
        const rawConfig = {
            timeToCount,
            interval,
            onStart,
            onUpdate,
            onEnd,
        };
        const config = omitBy(rawConfig, (val) => !val) as config;
        const example = new CountDown(config);

        example.start();
        setExample(example);

        return () => {
            example?.descroy();
        };
    }, [timeToCount]);

    return example;
}
